export function slugifyInterest(label) {
  const slug = String(label || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
  return slug || 'interest';
}

export function uniqueInterestId(label, existingIds) {
  const used = existingIds instanceof Set ? existingIds : new Set(existingIds);
  let base = slugifyInterest(label);
  let id = base;
  let suffix = 2;
  while (used.has(id)) {
    id = `${base}_${suffix}`;
    suffix += 1;
  }
  return id;
}

export function normalizeInterestItems(items) {
  if (!Array.isArray(items)) return [];
  return items
    .map((item) => ({
      id: String(item?.id || '').trim(),
      label: String(item?.label || '').trim(),
      emoji: String(item?.emoji ?? '').trim(),
    }))
    .filter((item) => item.id && item.label);
}

export function buildCatalogLookup(catalogItems) {
  const ids = new Set();
  const labelsLower = new Set();
  for (const item of catalogItems) {
    ids.add(item.id);
    labelsLower.add(item.label.toLowerCase());
  }
  return { ids, labelsLower };
}

export function isCatalogInterest(value, catalogItems) {
  if (!value) return false;
  const { ids, labelsLower } = buildCatalogLookup(catalogItems);
  const trimmed = String(value).trim();
  return ids.has(trimmed) || labelsLower.has(trimmed.toLowerCase());
}

export function aggregateCommunityInterests(users, catalogItems) {
  const { ids, labelsLower } = buildCatalogLookup(catalogItems);
  const counts = new Map();

  for (const user of users) {
    const interests = Array.isArray(user?.interests) ? user.interests : [];
    for (const interest of interests) {
      if (typeof interest !== 'string') continue;
      const trimmed = interest.trim();
      if (!trimmed) continue;
      if (ids.has(trimmed) || labelsLower.has(trimmed.toLowerCase())) continue;

      const key = trimmed.toLowerCase();
      if (!counts.has(key)) {
        counts.set(key, {
          label: trimmed,
          count: 0,
          sampleUsers: [],
        });
      }
      const entry = counts.get(key);
      entry.count += 1;
      if (entry.sampleUsers.length < 4) {
        entry.sampleUsers.push(user.username || user.email || user.id || 'User');
      }
    }
  }

  return Array.from(counts.values()).sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

export function resolveInterestLabel(value, catalogItems) {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  const match = catalogItems.find(
    (item) => item.id === trimmed || item.label.toLowerCase() === trimmed.toLowerCase()
  );
  return match ? match.label : trimmed;
}

/** Extract emoji characters only — never fall back to plain-text labels. */
function emojiFromCatalogMatch(match) {
  if (!match) return '';
  const direct = String(match.emoji || '').trim();
  if (direct) return direct;
  const label = String(match.label || '');
  const parts = label.match(/\p{Extended_Pictographic}+/gu);
  return parts ? parts.join('') : '';
}

/** Split an emoji string into individual glyphs (supports ZWJ sequences). */
export function splitInterestEmojiGlyphs(emojiStr) {
  const str = String(emojiStr || '').trim();
  if (!str) return [];
  const parts = str.match(/\p{Extended_Pictographic}(\uFE0F?\u200D\p{Extended_Pictographic})*/gu);
  return parts?.length ? parts : [str];
}

/** Text-only label for interest tooltips (strip leading emoji from catalog labels). */
export function interestTooltipLabel(label) {
  const text = String(label || '').trim();
  if (!text) return '';
  const withoutEmoji = text.replace(/\p{Extended_Pictographic}+/gu, '').trim();
  return withoutEmoji || text;
}

/** Map stored interest ids/labels to emoji + label pairs for card display. */
export function resolveInterestDisplayItems(values, catalogItems) {
  if (!Array.isArray(values) || !values.length) return [];
  const catalog = normalizeInterestItems(catalogItems);
  const items = [];
  const seen = new Set();

  values.forEach((value) => {
    const trimmed = String(value || '').trim();
    if (!trimmed) return;
    const match = catalog.find(
      (item) => item.id === trimmed || item.label.toLowerCase() === trimmed.toLowerCase()
    );
    const emoji = emojiFromCatalogMatch(match);
    if (!emoji) return;
    const label = match?.label || trimmed;
    const key = `${emoji}:${label}`;
    if (seen.has(key)) return;
    seen.add(key);
    items.push({ emoji, label });
  });

  return items;
}

/** Map stored interest ids/labels to emoji tokens for display (no text labels). */
export function resolveInterestEmojis(values, catalogItems) {
  return resolveInterestDisplayItems(values, catalogItems).map((item) => item.emoji);
}
